import { SortCondition } from 'shared/enums';
import { TReferencesInfo } from 'shared/types/reference.types';
import { edpApiV1 } from './index';

export type GetAllReferencesResponse = {
  data: TReferencesInfo[];
  pagination: {
    size: number;
    page: number;
    total: number;
  };
};

export type GetAllReferencesRequest = {
  size?: number;
  page?: number;
  type: string;
  includeOwn?: boolean;
  dateFrom?: string;
  dateTo?: string;
  refTypeId?: number;
  status?: string;
  order?: SortCondition;
  byId?: string;
  fio?: string;
  allAvailableOrganizations: boolean;
  personnelNumber?: string;
  positionName?: string;
};

export type GetReferenceResponse = TReferencesInfo;

export type ChangeConsultantRequest = {
  allAvailableOrganizations: boolean;
  requestId: number | string;
  hrId: number;
};

export type ChangeConsultantResponse = {
  data: string;
};

export type RejectReference = {
  requestId: number | string;
  rejectionReason: string;
  allAvailableOrganizations: boolean;
};

export type CommentsReference = {
  requestId: number;
  comment: string;
  allAvailableOrganizations: boolean;
};

export const referencesApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    getAllReferences: build.query<GetAllReferencesResponse, GetAllReferencesRequest>({
      query: params => ({ url: '/v1/admin/document/ref/requests', method: 'GET', params }),
      providesTags: ['references'],
    }),
    getReference: build.query<GetReferenceResponse, { requestId: string | number; allAvailableOrganizations: boolean }>(
      {
        query: ({ requestId, ...params }) => ({
          url: `/v1/admin/document/ref/requests/${requestId}`,
          method: 'GET',
          params,
        }),
        providesTags: ['singleReference'],
      },
    ),
    changeConsultantRef: build.mutation<ChangeConsultantResponse, ChangeConsultantRequest>({
      query: ({ requestId, allAvailableOrganizations, ...body }) => ({
        url: `/v1/admin/document/ref/requests/${requestId}`,
        method: 'PATCH',
        body,
        params: { allAvailableOrganizations },
      }),
      invalidatesTags: ['references', 'singleReference'],
    }),
    removeToHr: build.mutation<{ data: string }, { requestId: string | number; allAvailableOrganizations: boolean }>({
      query: ({ requestId, ...params }) => ({
        url: `/v1/admin/document/ref/requests/${requestId}/accountant-move-to-hr`,
        method: 'POST',
        params,
      }),
      invalidatesTags: ['references', 'singleReference'],
    }),
    hrClose: build.mutation<{ data: string }, { requestId: string | number; allAvailableOrganizations: boolean }>({
      query: ({ requestId, ...params }) => ({
        url: `/v1/admin/document/ref/requests/${requestId}/hr-close`,
        method: 'POST',
        params,
      }),
      invalidatesTags: ['references', 'singleReference'],
    }),
    hrTake: build.mutation<{ data: string }, { requestId: string | number; allAvailableOrganizations: boolean }>({
      query: ({ requestId, ...params }) => ({
        url: `/v1/admin/document/ref/requests/${requestId}/hr-take`,
        method: 'POST',
        params,
      }),
      invalidatesTags: ['references'],
    }),
    rejectRef: build.mutation<{ data: string }, RejectReference>({
      query: ({ requestId, allAvailableOrganizations, ...body }) => ({
        url: `/v1/admin/document/ref/requests/${requestId}/reject`,
        method: 'POST',
        body,
        params: { allAvailableOrganizations },
      }),
      invalidatesTags: ['references', 'singleReference'],
    }),
    commentsRef: build.mutation<{ data: string }, CommentsReference>({
      query: ({ requestId, allAvailableOrganizations, ...body }) => ({
        url: `/v1/admin/document/ref/requests/${requestId}`,
        method: 'PUT',
        body,
        params: { allAvailableOrganizations },
      }),
      invalidatesTags: ['singleReference'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllReferencesQuery,
  useLazyGetAllReferencesQuery,
  useChangeConsultantRefMutation,
  useGetReferenceQuery,
  useLazyGetReferenceQuery,
  useHrCloseMutation,
  useHrTakeMutation,
  useRejectRefMutation,
  useRemoveToHrMutation,
  useCommentsRefMutation,
} = referencesApi;
